"use client";

import { useState, useEffect } from "react";
import {
  Layout,
  Menu,
  Button,
  Drawer,
  Avatar,
  Dropdown,
  Badge,
  type MenuProps,
} from "antd";
import { MenuOutlined, DownOutlined } from "@ant-design/icons";
import { useRouter, usePathname } from "next/navigation";
import { useLanguage } from "@/context/LanguageContext";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { useClient } from "@/context/ClientContext";
import { useUserClients } from "@/hooks/useUserClients";
import { Image } from "antd";
import Icon from "@/components/common/Icon";
import CheckboxDropdown from "@/components/common/CheckboxDropdown";
import { SimpleBreadcrumb } from "@/components/common/SimpleBreadcrumb";
import { useStore } from "@/store/useStore";
import { useDashboardPermissions } from "@/hooks/useDashboardPermissions";
import { getFirstAvailablePage } from "@/utils/common";

const { Header, Sider, Content, Footer } = Layout;

export function DashboardLayout({ children }: { children: React.ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [logoSrc, setLogoSrc] = useState("/dim_logo.svg");
  const router = useRouter();
  const pathname = usePathname();
  const { t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const { user, loading, logout } = useAuth();
  const { allClients } = useClient();
  const { userClients } = useUserClients();
  const {
    setSelectedClientIds,
    setSelectedClientNames,
    selectedClientIds,
    hasInitializedClients,
    setHasInitializedClients,
    initializeDefaultDateRange
  } = useStore();
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  // Get dashboard permissions using the centralized logic
  const dashboardPermissions = useDashboardPermissions();

  // Check if user is admin
  const isAdmin = user?.roles?.some((role) => role.name === "admin") || false;

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !user) {
      router.replace("/login");
    }
  }, [user, loading, router]);

  // Redirect to first available page based on permissions
  useEffect(() => {
    if (!loading && user && pathname === "/dashboard") {
      const firstPage = getFirstAvailablePage(user);
      // Use centralized dashboard permission logic for redirect decision
      if (!dashboardPermissions.canViewDashboard && firstPage !== "/dashboard") {
        router.replace(firstPage);
      }
    }
  }, [user, loading, pathname, router, dashboardPermissions.canViewDashboard]);

  // Initialize default date range
  useEffect(() => {
    initializeDefaultDateRange();
  }, [initializeDefaultDateRange]);

  // Note: localStorage cleanup is now handled by zustand persist middleware migration

  // Auto-select all clients for admin users on initial load
  useEffect(() => {
    const initializeAdminClients = () => {
      // Only run if user is admin, authenticated, and we haven't initialized yet
      if (!isAdmin || !user || loading || hasInitializedClients) {
        return;
      }
      
      // Use existing clients data from infinite query instead of making a separate API call
      if (allClients.length > 0) {
        const accountIds = allClients
          .map((client) => client.account_uid)
          .filter(Boolean) as string[];

        // Check if there are any saved selections that should be preserved
        const hasSavedSelections = selectedClientIds.length > 0;

        if (!hasSavedSelections) {
          // First time or no saved selections - set all clients for admin
          setSelectedClientIds(accountIds);

          // Store client names
          const clientNamesMap = allClients.reduce((acc, client) => {
            if (client.account_uid && client.account_name) {
              acc[client.account_uid] = client.account_name;
            }
            return acc;
          }, {} as Record<string, string>);

          setSelectedClientNames(clientNamesMap);
        }
        
        // Mark as initialized
        setHasInitializedClients(true);
      }
    };

    initializeAdminClients();
  }, [isAdmin, user, loading, hasInitializedClients, selectedClientIds.length, allClients, setSelectedClientIds, setSelectedClientNames, setHasInitializedClients]);

  // Initialize non-admin users with their assigned clients
  useEffect(() => {
    const initializeNonAdminClients = async () => {
      // Only run if user is not admin, authenticated, and we haven't initialized yet
      // Also check if there are no selected clients (to avoid overriding restored selection)
      if (isAdmin || !user || loading || hasInitializedClients || selectedClientIds.length > 0) {
        return;
      }

      try {
        // Get user's assigned client IDs and names from account_access
        const userClientIds = userClients.map(client => client.accountUid);

        if (userClientIds.length > 0) {
          
          // Set the user's assigned clients (only when no clients are already selected)
          setSelectedClientIds(userClientIds);

          // Get client names from user's account_access (which has the real names from backend)
          const clientNamesMap = user.account_access?.reduce((acc, access) => {
              if (access.account_uid && access.account_name) {
                acc[access.account_uid] = access.account_name;
              }
              return acc;
            }, {} as Record<string, string>) || {};

          // Only set client names if they're not already set (to avoid overwriting login initialization)
          const { selectedClientNames: existingNames } = useStore.getState();
          if (Object.keys(existingNames).length === 0) {
            setSelectedClientNames(clientNamesMap);
          }
        }
      } catch (error) {
        // Silently handle error
      } finally {
        // Mark as initialized regardless of success/failure to prevent retries
        setHasInitializedClients(true);
      }
    };

    initializeNonAdminClients();
  }, [isAdmin, user, loading, hasInitializedClients, selectedClientIds.length, userClients, setSelectedClientIds, setHasInitializedClients]);

  useEffect(() => {
    setMounted(true);
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);


  // maintains admin submenu state
  useEffect(() => {
    if (pathname.startsWith('/admin/')) {
      setOpenKeys(['admin']);
    }
  }, [pathname]);

  useEffect(() => {
    // Reset hasInitializedClients on user change (login/logout)
    setHasInitializedClients(false);
  }, [user?.id]);


  // Don't render anything while checking authentication
  if (!mounted || loading || !user) {
    return null;
  }

  const handleMenuClick = (key: string) => {
    if (key === "/budget-pacing") {
      router.push("/budget-pacing");
      if (isMobile) setDrawerVisible(false);
      return;
    }
    if (key === pathname) {
      return; // Don't navigate if we're already on the page
    }
    if (isMobile) {
      setDrawerVisible(false);
    }
    router.push(key);
  };
    // Add this handler for submenu open/close
  const handleOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
  };

  const handleLogout = async () => {
    try {
      // Client selection state cleanup is now handled by clearUser() in the store
      await logout();
      router.push("/login");
    } catch (error) {
      // Fallback: redirect to login even if logout API fails
      router.push("/login");
    }
  };

  type MenuItem = Required<MenuProps>["items"][number];

  //Active icon coloring 
  const isAdminSectionActive = pathname.startsWith("/admin");
  const activeFilter =
    "brightness(0) saturate(100%) invert(23%) sepia(95%) saturate(5238%) hue-rotate(214deg) brightness(94%) contrast(99%)"; // #1A4BD2
  const inactiveFilter =
    "brightness(0) saturate(100%) invert(44%) sepia(0%) saturate(0%) hue-rotate(198deg) brightness(93%) contrast(88%)"; // grey

  // For regular items: key must match pathname exactly to be active
  const getIconFilter = (key: string) => (key === pathname ? activeFilter : inactiveFilter);

  // Build menu items dynamically based on user permissions
  const buildMenuItems = (): MenuItem[] => {
    const menuItems: MenuItem[] = [];
    const adminChildren: MenuItem[] = [];

    // Use centralized dashboard permission logic for consistency
    // This ensures dashboard menu follows same rules as dashboard route access and KPI access
    if (dashboardPermissions.canViewDashboard) {
      menuItems.push({
        key: "/dashboard",
        icon: (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 18,
              height: 18,
            }}
          >
            <Image
              src="/dashboard-icons/dashboard-icon.svg"
              alt="Dashboard"
              width={18}
              height={18}
              preview={false}
              style={{
                display: "block",
                margin: 0,
                padding: 0,
                filter: getIconFilter("/dashboard"),
                transition: "filter 0.2s ease",
              }}
            />
          </span>
        ),
        label: <span style={{ marginLeft: "10px" }}>{t("dashboard.title")}</span>,
      });
    }

    let masterReportAdded = false;

    user?.feature_access?.forEach((fa) => {
      const { feature, can_read } = fa;

      if (!can_read) return; // Skip if user doesn't have read permission

      switch (feature.name) {
        case "Master Report":
          menuItems.push({
            key: "/master-report",
            icon: (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 18,
                  height: 18,
                }}
              >
                <Image
                  src="/dashboard-icons/master-report-icon.svg"
                  alt="Master Report"
                  width={18}
                  height={18}
                  preview={false}
                  style={{
                    display: "block",
                    margin: 0,
                    padding: 0,
                    filter: getIconFilter("/master-report"),
                    transition: "filter 0.2s ease",
                  }}
                />
              </span>
            ),
            label: <span style={{ marginLeft: "10px" }}>{t("masterReport.title")}</span>,
          });
          masterReportAdded = true;

          // Add Amber Alerts right after Master Report for admin users
          if (isAdmin) {
            menuItems.push({
              key: "/amber-alert",
              icon: (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 18,
                    height: 18,
                  }}
                >
                  <Image
                    src="/dashboard-icons/Amber-Alert.svg"
                    alt="Amber Alert"
                    width={18}
                    height={18}
                    preview={false}
                    style={{
                      display: "block",
                      margin: 0,
                      padding: 0,
                      filter: getIconFilter("/amber-alert"),
                      transition: "filter 0.2s ease",
                    }}
                  />
                </span>
              ),
              label: <span style={{ marginLeft: "10px" }}>{t("amberAlert.title")}</span>,
            });
          }

          // Add Budget Pacing right after Amber Alerts
          if (dashboardPermissions.canAccessBudgetPage && !menuItems.some((m) => m?.key === "/budget-pacing")) {
            menuItems.push({
              key: "/budget-pacing",
              icon: (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 18,
                    height: 18,
                  }}
                >
                  <Image
                    src="/dashboard-icons/Budget.svg"
                    alt="Budget Pacing"
                    width={18}
                    height={18}
                    preview={false}
                    style={{
                      display: "block",
                      margin: 0,
                      padding: 0,
                      filter: getIconFilter("/budget-pacing"),
                      transition: "filter 0.2s ease",
                    }}
                  />
                </span>
              ),
              label: <span style={{ marginLeft: "10px" }}>{t("budgetPacing.title")}</span>,
            });
          }
          break;

        case "Budget Pacing":
          if (dashboardPermissions.canAccessBudgetPage && !menuItems.some((m) => m?.key === "/budget-pacing")) {
            menuItems.push({
              key: "/budget-pacing",
              icon: (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 18,
                    height: 18,
                  }}
                >
                  <Image
                    src="/dashboard-icons/Budget.svg"
                    alt="Budget Pacing"
                    width={18}
                    height={18}
                    preview={false}
                    style={{
                      display: "block",
                      margin: 0,
                      padding: 0,
                      filter: getIconFilter("/budget-pacing"),
                      transition: "filter 0.2s ease",
                    }}
                  />
                </span>
              ),
              label: <span style={{ marginLeft: "10px" }}>{t("budgetPacing.title")}</span>,
            });
          }
          break;

        case "amber-alerts":
        case "Amber Alerts":
          // Only add for non-admin users (admin already has it added above)
          if (!isAdmin) {
            menuItems.push({
              key: "/amber-alert",
              icon: (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 18,
                    height: 18,
                  }}
                >
                  <Image
                    src="/dashboard-icons/Amber-Alert.svg"
                    alt="Amber Alert"
                    width={18}
                    height={18}
                    preview={false}
                    style={{
                      display: "block",
                      margin: 0,
                      padding: 0,
                      filter: getIconFilter("/amber-alert"),
                      transition: "filter 0.2s ease",
                    }}
                  />
                </span>
              ),
              label: <span style={{ marginLeft: "10px" }}>{t("amberAlert.title")}</span>,
            });
            // Add Budget Pacing right after Amber Alerts
            if (dashboardPermissions.canAccessBudgetPage && !menuItems.some((m) => m?.key === "/budget-pacing")) {
              menuItems.push({
                key: "/budget-pacing",
                icon: (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 18,
                      height: 18,
                    }}
                  >
                    <Image
                      src="/dashboard-icons/Budget.svg"
                      alt="Budget Pacing"
                      width={18}
                      height={18}
                      preview={false}
                      style={{
                        display: "block",
                        margin: 0,
                        padding: 0,
                        filter: getIconFilter("/budget-pacing"),
                        transition: "filter 0.2s ease",
                      }}
                    />
                  </span>
                ),
                label: <span style={{ marginLeft: "10px" }}>{t("budgetPacing.title")}</span>,
              });
            }
          }
          break;

        case "Users":
          adminChildren.push({
            key: "/admin/users",
            label: <span style={{ marginLeft: "10px" }}>{t("admin.users")}</span>,
          });
          break;

        case "Roles":
          adminChildren.push({
            key: "/admin/roles",
            label: <span style={{ marginLeft: "10px" }}>{t("admin.features")}</span>,
          });
          break;

        case "Row Level Settings":
          adminChildren.push({
            key: "/admin/row-level-settings",
            label: <span style={{ marginLeft: "10px" }}>{t("admin.rowLevelSettings")}</span>,
          });
          break;

        // Add more feature cases as needed
        default:
          break;
      }
    });

    // If admin user doesn't have Master Report access, still add Amber Alerts and Budget Pacing at the end
    if (isAdmin && !masterReportAdded) {
      menuItems.push({
        key: "/amber-alert",
        icon: (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 18,
              height: 18,
            }}
          >
            <Image
              src="/dashboard-icons/Amber-Alert.svg"
              alt="Amber Alert"
              width={18}
              height={18}
              preview={false}
              style={{
                display: "block",
                margin: 0,
                padding: 0,
                filter: getIconFilter("/amber-alert"),
                transition: "filter 0.2s ease",
              }}
            />
          </span>
        ),
        label: <span style={{ marginLeft: "10px" }}>{t("amberAlert.title")}</span>,
      });
      if (dashboardPermissions.canAccessBudgetPage && !menuItems.some((m) => m?.key === "/budget-pacing")) {
        menuItems.push({
          key: "/budget-pacing",
          icon: (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 18,
                height: 18,
              }}
            >
              <Image
                src="/dashboard-icons/Budget.svg"
                alt="Budget Pacing"
                width={18}
                height={18}
                preview={false}
                style={{
                  display: "block",
                  margin: 0,
                  padding: 0,
                  filter: getIconFilter("/budget-pacing"),
                  transition: "filter 0.2s ease",
                }}
              />
            </span>
          ),
          label: <span style={{ marginLeft: "10px" }}>{t("budgetPacing.title")}</span>,
        });
      }
    }

    // Admin section
    if (adminChildren.length > 0) {
      menuItems.push({
        key: "admin",
        icon: (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 18,
              height: 18,
            }}
          >
            <Image
              src="/dashboard-icons/Vector.svg"
              alt="Admin"
              width={18}
              height={18}
              preview={false}
              style={{
                display: "block",
                margin: 0,
                padding: 0,
                // ✅ Admin icon is active for any /admin/* route
                filter: isAdminSectionActive ? activeFilter : inactiveFilter,
                transition: "filter 0.2s ease",
              }}
            />
          </span>
        ),
        label: <span style={{ marginLeft: "20px" }}>{t("admin.title")}</span>,
        children: adminChildren,
      });
    }

    return menuItems;
  };

  const menuItems = buildMenuItems();

  // Get user's name initial for avatar
  const getUserInitial = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(
        0
      )}`.toUpperCase();
    } else if (user?.first_name) {
      return user.first_name.charAt(0).toUpperCase();
    } else if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return "U";
  };

  const userMenu = {
    items: [
      {
        key: "user-info",
        label: (
          <div className="px-2 py-1">
            <div className="font-medium text-gray-900">
              {user?.first_name && user?.last_name
                ? `${user.first_name} ${user.last_name}`
                : user?.first_name || "User"}
            </div>
            <div className="text-sm text-gray-500 truncate">
              {user?.email || ""}
            </div>
          </div>
        ),
        disabled: true,
      },
      {
        type: "divider" as const,
      },
      {
        key: "profile",
        icon: <Icon name="userOutlined" />,
        label: t("dashboard.edit.profile"),
        onClick: () => router.push("/profile"),
      },
      {
        key: "logout",
        icon: <Icon name="logout" width="14px" />,
        label: t("dashboard.logout"),
        onClick: handleLogout,
      },
    ],
  };

  const toggleLogo = () => {
    setLogoSrc((prev) =>
      prev === "/dim_logo.svg" ? "/logo_icon.svg" : "/dim_logo.svg"
    );
  };

  const Sidebar = () => (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      width={256}
      style={{
        overflow: "auto",
        height: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        bottom: 0,
        background: theme === "dark" ? "rgb(20, 20, 20)" : "#fff",
      }}
      theme={theme}
    >
      <div
        className="h-16 flex items-center justify-center p-2"
        style={{
          background: theme === "dark" ? "rgb(20, 20, 20)" : "#1A4BD2",
        }}
      >
        <div
          className="flex items-center justify-center w-full h-full cursor-pointer"
          role="link"
          tabIndex={0}
          aria-label={t("dashboard.title")}
          onClick={() => handleMenuClick("/dashboard")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleMenuClick("/dashboard");
            }
          }}
        >
          <Image
            width={collapsed ? 80 : 140}
            height={collapsed ? 80 : 50}
            src={logoSrc}
            preview={false}
            alt="Logo"
            // onClick={toggleLogo}
            style={{
              cursor: "pointer",
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              marginLeft: collapsed ? '20px' : 0,
            }}
          />
        </div>
      </div>
      <Menu
        theme={theme}
        mode="inline"
        selectedKeys={[pathname]}
        openKeys={openKeys}
        onOpenChange={handleOpenChange}
        items={menuItems}
        onClick={({ key }) => handleMenuClick(key)}
        style={{
          background: theme === "dark" ? "rgb(20, 20, 20)" : "#fff",
        }}
      />
    </Sider>
  );

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {isMobile ? (
        <Drawer
          placement="left"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          bodyStyle={{ padding: 0 }}
          width={256}
          closable={false}
        >
          <Sidebar />
        </Drawer>
      ) : (
        <Sidebar />
      )}

      <Layout style={{ marginLeft: isMobile ? 0 : collapsed ? 80 : 256 }}>
        <Header
          style={{
            padding: "0 16px",
            background: theme === "dark" ? "#141414" : "#1A4BD2",
            position: "sticky",
            top: 0,
            zIndex: 10,
            width: "100%",
            display: "flex",
            alignItems: "center",
            height: "64px",
          }}
        >
          <div className="flex items-center h-full">
            {isMobile ? (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => {
                  setDrawerVisible(true);
                  toggleLogo();
                }}
                style={{
                  fontSize: "16px",
                  width: 64,
                  height: 64,
                  color: "white",
                }}
              />
            ) : (
              <Button
                type="text"
                icon={collapsed ? <MenuOutlined /> : <MenuOutlined />}
                onClick={() => {
                  setCollapsed(!collapsed);
                  toggleLogo();
                }}
                style={{
                  fontSize: "16px",
                  width: 64,
                  height: 64,
                  color: "white",
                }}
              />
            )}
          </div>

          <div className="flex-1 flex justify-end">
            <CheckboxDropdown
              useZustandMulti={true}
              contextId="dashboard"
              placeholder="Select clients..."
              userClientIds={isAdmin ? undefined : userClients.map((client) => client.accountUid)}
              isAdmin={isAdmin}
            />
          </div>

          <div className="flex items-center gap-4 h-full">
            {/* TODO: Add theme toggle for next phase */}
            {/* <Button
              icon={<Icon name="moon" style={{ color: "white" }} />}
              onClick={toggleTheme}
              type="text"
              style={{
                fontSize: 16,
                width: 41,
                height: 41,
                borderRadius: "50%",
                backgroundColor: "#00000026",
                border: "none",
              }}
            /> */}
            {/* <Badge count={3} offset={[-5, 5]}>
              <Button
                icon={<Icon name="bellOutlined" style={{ color: "white" }} />}
                onClick={() => {}}
                type="text"
                style={{
                  fontSize: 16,
                  width: 41,
                  height: 41,
                  borderRadius: "50%",
                  backgroundColor: "#00000026",
                  border: "none",
                }}
              />
            </Badge> */}
            <Dropdown menu={userMenu} placement="bottomRight">
              <div className="flex items-center cursor-pointer">
                <Avatar
                  style={{
                    width: "40px",
                    height: "40px",
                    backgroundColor: "#1890ff",
                    color: "#fff",
                    fontSize: "16px",
                    fontWeight: "bold",
                  }}
                >
                  {getUserInitial()}
                </Avatar>
                <DownOutlined
                  style={{
                    marginLeft: "8px",
                    color: theme === "dark" ? "#000 !important" : "#fff",
                  }}
                />
              </div>
            </Dropdown>
          </div>
        </Header>
        <Content
          style={{
            margin: 0,
            padding: 24,
            minHeight: "calc(100vh - 128px)",
            background: theme === "dark" ? "#141414" : "#EEF1F3",
          }}
        >
          <SimpleBreadcrumb />
          {children}
        </Content>
        <Footer
          style={{
            textAlign: "center",
            background: theme === "dark" ? "#141414" : "#fff",
            borderTop: `1px solid ${theme === "dark" ? "#303030" : "#f0f0f0"}`,
          }}
        >
          {t("app.footer").replace(
            "{year}",
            String(new Date().getFullYear())
          )}
        </Footer>
      </Layout>
    </Layout>
  );
}
